/**
 * Reusable Custom Domain manager dialog.
 * Used by Super Admin (tenant dashboard) and Tenant Admin (own settings).
 *
 * Props:
 *  - open, onOpenChange: dialog state
 *  - tenant: { id, name, custom_domain, custom_domain_meta }
 *  - onChange: callback after add/remove/refresh — parent refetches
 */
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Globe, Copy, RefreshCw, Trash2, CheckCircle2, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { tenantPortalUrl } from '../lib/tenantDetection';

const STATUS_META = {
  pending_dns: { label: 'Pending DNS', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock },
  verifying: { label: 'Verifying', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: RefreshCw },
  active: { label: 'Active', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending_dns;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${meta.color}`} data-testid={`domain-status-${status}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </Badge>
  );
}

function CopyRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between bg-white border rounded p-2 gap-2">
      <div className="min-w-0">
        <p className="text-[10px] text-muted-foreground">{label}</p>
        <p className="font-mono text-xs truncate" data-testid={testid}>{value}</p>
      </div>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 flex-shrink-0"
        onClick={() => { navigator.clipboard.writeText(value); toast.success('Copied!'); }}
      >
        <Copy className="w-3 h-3" />
      </Button>
    </div>
  );
}

export default function CustomDomainDialog({ open, onOpenChange, tenant, onChange }) {
  const { api } = useAuth();
  const [domain, setDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const meta = tenant?.custom_domain_meta;
  const hasDomain = !!tenant?.custom_domain && !!meta;

  const handleAdd = async () => {
    const d = domain.trim().toLowerCase();
    if (!d || !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(d)) {
      toast.error('Enter a valid domain (e.g., learn.acme.com)');
      return;
    }
    setAdding(true);
    try {
      await api.post(`/admin/tenants/${tenant.id}/custom-domain`, { domain: d });
      toast.success('Domain added — share DNS records with the tenant');
      setDomain('');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.get(`/admin/tenants/${tenant.id}/custom-domain/status`);
      toast.success('Status refreshed');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove custom domain "${tenant.custom_domain}"?`)) return;
    setRemoving(true);
    try {
      await api.delete(`/admin/tenants/${tenant.id}/custom-domain`);
      toast.success('Domain removed');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="custom-domain-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Custom Domain — {tenant?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {hasDomain ? (
            <>
              {/* Current Domain */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Active Domain</p>
                      <p className="font-mono text-lg font-semibold">{tenant.custom_domain}</p>
                    </div>
                    <StatusBadge status={meta.status || 'pending_dns'} />
                  </div>

                  {meta.status === 'active' && (
                    <div className="flex gap-2">
                      <Button asChild size="sm" variant="outline">
                        <a href={`https://${tenant.custom_domain}`} target="_blank" rel="noreferrer" className="gap-1">
                          Visit <ExternalLink className="w-3 h-3" />
                        </a>
                      </Button>
                    </div>
                  )}

                  {meta.error_message && (
                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 p-2 rounded">
                      {meta.error_message}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* DNS Records */}
              {(meta.status !== 'active' || meta.dns_records?.length > 0) && (
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <p className="text-sm font-semibold">Required DNS Records</p>
                    <p className="text-xs text-muted-foreground">
                      Tenant must add these at their domain registrar (Cloudflare, GoDaddy, Namecheap, etc.)
                    </p>
                    {(meta.dns_records || []).map((rec, idx) => (
                      <div key={idx} className="bg-muted/30 p-3 rounded space-y-2 border">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="font-mono">{rec.recordType}</Badge>
                          <StatusBadge status={(rec.status || '').toLowerCase() === 'valid' ? 'active' : 'pending_dns'} />
                        </div>
                        <CopyRow label="Host / Name" value={rec.fqdn} testid={`dns-fqdn-${idx}`} />
                        <CopyRow label="Value / Target" value={rec.value} testid={`dns-value-${idx}`} />
                        {rec.currentValue && rec.currentValue !== rec.value && (
                          <p className="text-[10px] text-amber-700">
                            Currently resolves to: <code className="bg-amber-50 px-1">{rec.currentValue}</code>
                          </p>
                        )}
                      </div>
                    ))}
                    {(meta.dns_records || []).length === 0 && meta.cname_target && (
                      <div className="bg-muted/30 p-3 rounded space-y-2 border">
                        <Badge variant="secondary" className="font-mono">CNAME</Badge>
                        <CopyRow label="Host" value={meta.cname_fqdn || tenant.custom_domain} testid="dns-cname-host" />
                        <CopyRow label="Target" value={meta.cname_target} testid="dns-cname-target" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-domain-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh Status
                </Button>
                <Button variant="destructive" onClick={handleRemove} disabled={removing} data-testid="remove-domain-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  {removing ? 'Removing...' : 'Remove Domain'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Card className="border-dashed">
                <CardContent className="p-4 space-y-3">
                  <div className="space-y-2">
                    <Label>Domain</Label>
                    <Input
                      placeholder="learn.acme.com"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value.toLowerCase())}
                      data-testid="domain-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the domain without <code>http://</code>. We'll register it with Railway automatically.
                    </p>
                  </div>
                  <Button onClick={handleAdd} disabled={adding || !domain.trim()} data-testid="add-domain-btn">
                    {adding ? 'Registering with Railway...' : 'Add Custom Domain'}
                  </Button>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground text-center">
                Tip: the portal already works at <code className="bg-muted px-1">{tenantPortalUrl({ ...tenant, custom_domain: null })}</code> with no DNS setup at all. A custom domain is only for a branded address.
              </p>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
